/**
 * CreditChecksPanel — file PMS enquêtes de crédit (rapport de locataire DG · dépôt d'un PDF externe).
 * Batch #14.154 — extracted from AdminPmsQueuePage.jsx during refactor.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Shield, CheckCircle2, AlertCircle, Clock, X, Upload, FileText,
  ChevronRight, Loader2, Trash2, FileSignature,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "./_shared";
import ArchiveButton from "@/components/admin/ArchiveButton";
import AdminTaskStatusSelector from "@/components/admin/AdminTaskStatusSelector";

function StatusBadge({ status }) {
  const map = {
    pending:     { label: "En attente", cls: "bg-amber-100 text-amber-700" },
    in_progress: { label: "En cours",   cls: "bg-blue-100 text-blue-700" },
    completed:   { label: "Terminée",   cls: "bg-emerald-100 text-emerald-700" },
    rejected:    { label: "Rejetée",    cls: "bg-rose-100 text-rose-700" },
    cancelled:   { label: "Annulée",    cls: "bg-slate-200 text-slate-700" },
  };
  const s = map[status] || { label: status, cls: "bg-slate-100 text-slate-600" };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

function CreditStatusBadge({ status }) {
  const m = {
    pending:     { bg: "bg-amber-100 text-amber-800",     label: "En attente" },
    in_progress: { bg: "bg-blue-100 text-blue-800",       label: "En cours" },
    in_analysis: { bg: "bg-blue-100 text-blue-800",       label: "En cours d'analyse" },
    completed:   { bg: "bg-emerald-100 text-emerald-800", label: "Terminé" },
    approved:    { bg: "bg-emerald-100 text-emerald-800", label: "Accepté" },
    rejected:    { bg: "bg-rose-100 text-rose-800",       label: "Refusé" },
    refused:     { bg: "bg-rose-100 text-rose-800",       label: "Refusé" },
    cancelled:   { bg: "bg-slate-200 text-slate-700",     label: "Annulé" },
  }[status] || { bg: "bg-slate-100 text-slate-700", label: status };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${m.bg}`}>{m.label}</span>;
}

function CopyField({ label, value, mono = false, testid }) {
  const [copied, setCopied] = useState(false);
  const onClick = () => {
    if (!value || value === "—") return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-cyan-700 font-semibold">{label}</div>
      <button
        onClick={onClick}
        type="button"
        title="Cliquez pour copier"
        className={`group w-full text-left mt-0.5 px-2 py-1 rounded border border-cyan-100 bg-white hover:bg-cyan-50 transition text-slate-800 inline-flex items-center justify-between ${mono ? "font-mono" : ""}`}
        data-testid={testid}
      >
        <span className="truncate">{value || "—"}</span>
        {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" /> : <FileText className="w-3 h-3 text-cyan-400 opacity-0 group-hover:opacity-100 transition flex-shrink-0" />}
      </button>
    </div>
  );
}

function CreditQuickActions({ item, onRefresh, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const quickStatus = async (newStatus) => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/pms/credit-checks/${item.id}`, { status: newStatus }, { headers: authH() });
      toast.success(`Statut → ${newStatus}`);
      onRefresh?.(); onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const deleteCheck = async () => {
    if (!window.confirm("Supprimer définitivement cette enquête ?\n(Elle disparaît de la file ; l'audit reste conservé en backup.)")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/pms/credit-checks/${item.id}`, { headers: authH() });
      toast.success("Enquête supprimée");
      onRefresh?.(); onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex gap-1 items-center">
      {item.status === "pending" && (
        <button
          onClick={() => quickStatus("in_progress")}
          disabled={busy}
          className="text-[10px] px-2 py-1 rounded-lg border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 inline-flex items-center gap-1 flex-shrink-0"
          title="Démarrer le traitement"
          data-testid={`admin-credit-start-${item.id}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
          Démarrer
        </button>
      )}
      {item.status === "in_progress" && (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 inline-flex items-center gap-1 flex-shrink-0">
          <Loader2 className="w-3 h-3 animate-spin" /> En traitement
        </span>
      )}
      {(item.status === "pending" || item.status === "rejected" || !item.status) && (
        <button
          onClick={deleteCheck}
          disabled={busy}
          className="p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40 flex-shrink-0"
          title="Supprimer (erreur de saisie / test)"
          data-testid={`admin-credit-delete-${item.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function CreditCheckDetailModal({ item, onClose, onUpdated }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    status: item.status || "pending",
    admin_notes: item.admin_notes || item.result_summary || "",
  });
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const fullAddress = item.candidate_current_address ||
    [
      `${item.candidate_address_civic_number || ""} ${item.candidate_address_street || ""}`.trim(),
      item.candidate_address_label,
      item.candidate_address_city,
      item.candidate_address_postal_code,
    ].filter(Boolean).join(", ");

  const copyBlock = () => {
    const lines = [
      `Prénom : ${item.candidate_first_name || ""}`,
      `Nom : ${item.candidate_last_name || ""}`,
      `Date de naissance : ${item.candidate_dob || ""}`,
      `Téléphone : ${item.candidate_phone || ""}`,
      `Courriel : ${item.candidate_email || ""}`,
      `Adresse : ${fullAddress}`,
    ].join("\n");
    navigator.clipboard.writeText(lines).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Informations copiées dans le presse-papier");
    }).catch(() => toast.error("Copie impossible"));
  };

  const save = async (forceStatus = null) => {
    setBusy(true);
    try {
      const payload = {
        status: forceStatus || form.status,
        admin_notes: form.admin_notes || null,
        result_summary: form.admin_notes || null,
      };
      await axios.put(`${API}/admin/pms/credit-checks/${item.id}`, payload, { headers: authH() });
      const finalStatuses = ["completed", "approved", "rejected", "refused"];
      const isFinal = finalStatuses.includes(payload.status);
      toast.success(isFinal ? "Verdict enregistré · propriétaire notifié" : "Enquête mise à jour");
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const uploadReport = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/pms/credit-checks/${item.id}/upload-report`, fd, {
        headers: { ...authH(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Rapport déposé dans le coffre du client");
      setFile(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  const statusButtons = [
    { value: "in_analysis", label: "En cours d'analyse", icon: Clock,        bg: "bg-blue-600 hover:bg-blue-700" },
    { value: "approved",    label: "Accepté",            icon: CheckCircle2, bg: "bg-emerald-600 hover:bg-emerald-700" },
    { value: "refused",     label: "Refusé",             icon: X,            bg: "bg-rose-600 hover:bg-rose-700" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="admin-credit-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between bg-cyan-50">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-cyan-600" />
            <div>
              <h3 className="font-bold text-slate-800 text-sm">{item.candidate_first_name} {item.candidate_last_name}</h3>
              <p className="text-[10px] text-slate-500">
                Demande #{item.id?.slice(0, 8)} · Master : <b>{item._master_name || "—"}</b> · Soumis le {item.created_at?.slice(0, 10)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white rounded-lg" data-testid="admin-credit-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="rounded-xl border-2 border-cyan-200 bg-cyan-50/40 p-4 space-y-3" data-testid="admin-credit-candidate-card">
            <div className="flex items-center justify-between">
              <h4 className="text-[11px] uppercase font-bold text-cyan-800 tracking-wider">
                Informations candidat (pour le fournisseur d'enquête)
              </h4>
              <button
                onClick={copyBlock}
                className="text-[10px] px-2 py-1 rounded-md bg-white border border-cyan-300 text-cyan-700 hover:bg-cyan-100 inline-flex items-center gap-1 font-semibold"
                data-testid="admin-credit-copy-all"
              >
                {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <FileText className="w-3 h-3" />}
                {copied ? "Copié !" : "Copier tout"}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <CopyField label="Prénom" value={item.candidate_first_name} testid="admin-credit-firstname" />
              <CopyField label="Nom" value={item.candidate_last_name} testid="admin-credit-lastname" />
              <CopyField label="Date de naissance" value={item.candidate_dob} testid="admin-credit-dob" />
              <CopyField label="Téléphone" value={item.candidate_phone} testid="admin-credit-phone" />
              <CopyField label="Courriel" value={item.candidate_email} testid="admin-credit-email" />
              <CopyField label="N° civique" value={item.candidate_address_civic_number} />
              <CopyField label="Rue" value={item.candidate_address_street} />
              <CopyField label="Libellé" value={item.candidate_address_label || "—"} />
              <CopyField label="Ville" value={item.candidate_address_city} />
              <CopyField label="Code postal" value={item.candidate_address_postal_code} mono />
              <div className="sm:col-span-2">
                <CopyField label="Adresse complète" value={fullAddress} />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-cyan-200 text-[10px]">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
              <span className="text-slate-600">
                Consentement obtenu attesté par le propriétaire le{" "}
                <b>{item.consent_obtained_at?.slice(0, 16).replace("T", " à ") || "—"}</b>
              </span>
            </div>

            {item.payment_status === "paid" ? (
              <div className="flex items-center gap-2 pt-1 border-t border-cyan-200 text-[10px] text-emerald-700">
                <CheckCircle2 className="w-3 h-3" />
                <span><b>Paiement Stripe reçu</b> ({Number(item.payment_amount || 0).toFixed(2)} $)</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 pt-1 border-t border-cyan-200 text-[10px] text-amber-700">
                <AlertCircle className="w-3 h-3" />
                <span>Paiement Stripe non confirmé</span>
              </div>
            )}

            {item.notes && (
              <div className="rounded bg-white border border-slate-200 p-2 mt-1 text-[11px]">
                <span className="text-slate-500 font-semibold">Note du propriétaire :</span>{" "}
                <span className="italic">{item.notes}</span>
              </div>
            )}
          </div>

          <div className="rounded-xl border-2 border-teal-200 bg-teal-50/40 p-3 flex flex-wrap items-center gap-3" data-testid="admin-credit-report-card">
            <FileSignature className="w-5 h-5 text-teal-700 shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <h4 className="text-[11px] uppercase font-bold text-teal-800 tracking-wider">Rapport de locataire DG</h4>
              <p className="text-[11px] text-slate-600">
                {item.report_generated_at
                  ? <>Rapport généré et envoyé le <b>{item.report_generated_at.slice(0, 10)}</b>{item.result_score ? <> · cote {item.result_score}</> : null}</>
                  : item.report_meta?.updated_at ? <>Brouillon en cours (modifié le {item.report_meta.updated_at.slice(0, 10)})</> : <>Transcrivez le rapport du fournisseur (pré-remplissage IA depuis le PDF) et générez votre rapport à vos couleurs.</>}
              </p>
            </div>
            <button
              onClick={() => navigate(`/admin/pms-queue/enquete/${item.id}/rapport`)}
              className="px-3 py-1.5 rounded-lg bg-teal-700 text-white text-xs font-semibold hover:bg-teal-800 inline-flex items-center gap-1.5"
              data-testid="admin-credit-report-btn"
            >
              <FileSignature className="w-3.5 h-3.5" /> {item.report_generated_at || item.report_meta?.updated_at ? "Ouvrir le rapport" : "Rédiger le rapport"}
            </button>
          </div>

          <div className="rounded-xl border border-slate-200 p-3 space-y-2">
            <h4 className="text-[11px] uppercase font-bold text-slate-700 tracking-wider">Statut de la demande</h4>
            <div className="flex items-center gap-2">
              <CreditStatusBadge status={form.status} />
              {item.completed_at && (
                <span className="text-[10px] text-slate-400">
                  Finalisée le {item.completed_at?.slice(0, 10)} par {item.completed_by || "admin"}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-[11px] uppercase font-bold text-slate-700 tracking-wider">
              Notes de l'Admin
            </label>
            <textarea
              rows={4}
              value={form.admin_notes}
              onChange={(e) => upd("admin_notes", e.target.value)}
              maxLength={3000}
              className="cc-input"
              placeholder="Résumé de l'analyse, score, recommandation, etc."
              data-testid="admin-credit-notes"
            />
            <div className="text-[10px] text-slate-400 text-right">{form.admin_notes.length}/3000</div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/30 p-3 space-y-2">
            <h4 className="text-[11px] uppercase font-bold text-slate-700 tracking-wider">Déposer un autre PDF (rapport externe)</h4>
            <p className="text-[10px] text-slate-500">Sera déposé dans le coffre-fort du propriétaire.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="cc-input flex-1 text-xs" data-testid="admin-credit-upload-file" />
              <button
                onClick={uploadReport}
                disabled={!file || uploading}
                className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid="admin-credit-upload-submit"
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Téléverser
              </button>
            </div>
            {item.report_safe_doc_id && (
              <div className="text-[10px] text-emerald-700 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Rapport déjà déposé le {item.report_uploaded_at?.slice(0, 10)}
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-slate-50">
          <div className="text-[10px] uppercase font-bold text-slate-700 tracking-wider mb-2">
            Définir le statut & notifier le propriétaire
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {statusButtons.map((b) => (
              <button
                key={b.value}
                onClick={() => { upd("status", b.value); save(b.value); }}
                disabled={busy}
                className={`px-3 py-1.5 text-xs rounded-lg text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 transition ${b.bg}`}
                data-testid={`admin-credit-status-${b.value}`}
              >
                {busy && form.status === b.value ? <Loader2 className="w-3 h-3 animate-spin" /> : <b.icon className="w-3 h-3" />}
                {b.label}
              </button>
            ))}
            <div className="flex-1" />
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border bg-white hover:bg-slate-100" data-testid="admin-credit-cancel">Annuler</button>
            <button
              onClick={() => save(null)}
              disabled={busy}
              className="px-4 py-1.5 text-sm rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="admin-credit-save"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Enregistrer & notifier
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreditChecksPanel({ onUpdate }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pms/credit-checks${statusFilter ? `?status=${statusFilter}` : ""}`, { headers: authH() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  return (
    <>
      <div className="px-5 py-3 border-b bg-slate-50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-cyan-600" />
          <h2 className="font-semibold text-slate-800 text-sm">Enquêtes de crédit</h2>
        </div>
        <div className="flex items-center gap-1">
          {[
            { v: "pending",     label: "En attente" },
            { v: "in_progress", label: "En cours" },
            { v: "completed",   label: "Terminées" },
            { v: "",            label: "Toutes" },
          ].map((s) => (
            <button
              key={s.v}
              onClick={() => setStatusFilter(s.v)}
              className={`text-[11px] px-2 py-0.5 rounded-full transition ${statusFilter === s.v ? "bg-cyan-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-3">
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-400 italic">Aucune enquête {statusFilter || ""}</div>
        ) : (
          <div className="space-y-1.5">
            {items.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-cyan-300 hover:bg-cyan-50/30 transition"
                data-testid={`admin-credit-row-${c.id}`}
              >
                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => setSelectedItem(c)}
                >
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 text-sm">{c.candidate_first_name} {c.candidate_last_name}</span>
                    <StatusBadge status={c.status} />
                    {c.result_score && (
                      <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                        Score {c.result_score}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate mt-0.5">
                    Master : <b>{c._master_name || "?"}</b>
                    {c.candidate_email && <span> · {c.candidate_email}</span>}
                    {c.candidate_monthly_income && <span> · Revenu {c.candidate_monthly_income.toLocaleString("fr-CA")} $/mois</span>}
                    <span> · {c.created_at?.slice(0, 10)}</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/admin/pms-queue/enquete/${c.id}/rapport`)}
                  className={`p-1.5 rounded-lg ${c.report_generated_at ? "text-emerald-700 hover:bg-emerald-50" : "text-teal-700 hover:bg-teal-50"}`}
                  title={c.report_generated_at ? "Rapport de locataire envoyé — ouvrir" : "Rédiger le rapport de locataire"}
                  data-testid={`admin-credit-report-${c.id}`}
                >
                  <FileSignature className="w-4 h-4" />
                </button>
                <CreditQuickActions item={c} onRefresh={fetchItems} onUpdate={onUpdate} />
                <AdminTaskStatusSelector
                  itemType="credit_check"
                  itemId={c.id}
                  value={c.admin_task_status}
                  onChanged={fetchItems}
                />
                <ArchiveButton
                  itemType="credit_check"
                  itemId={c.id}
                  itemLabel="cette enquête de crédit"
                  onArchived={fetchItems}
                  size="xs"
                />
                <button
                  onClick={() => setSelectedItem(c)}
                  className="p-1.5 rounded-lg hover:bg-cyan-100 text-cyan-700"
                  title="Détails et upload du rapport"
                  data-testid={`admin-credit-open-${c.id}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedItem && (
        <CreditCheckDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdated={() => { setSelectedItem(null); fetchItems(); onUpdate?.(); }}
        />
      )}
    </>
  );
}
